import React from "react";
import { Link } from "react-router-dom";
import { Check, Lock } from "lucide-react";
import { PERSONA_PLANS } from "../../data/personaPlans";

/**
 * The persona's 60-Day Financial Plan on the quiz results page.
 * Weeks 1–4 are the persona's habit actions (week 1 open; weeks 2–4 title +
 * teaser public, actions blurred for visitors). A final "Days 29–60" row
 * ties the plan to the member's own Spending Plan. Signup CTAs live in
 * ResultCta (top / preview / bottom), not here.
 */
export const PersonaPlan = ({ slug, name, color, isMember }) => {
  const plan = PERSONA_PLANS[slug];
  if (!plan) return null;
  const shortName = name.replace(/^The /, "");

  return (
    <section className="mt-12" data-testid="result-persona-plan">
      <p className="eyebrow text-[#A9712A]">Your 60-Day Financial Plan</p>
      <h2 className="font-serif text-3xl sm:text-4xl text-[#2C302E] mt-2 leading-tight">
        The {shortName} plan: 60 days, one habit at a time.
      </h2>
      <p className="mt-4 text-[#4d4f4c] leading-relaxed max-w-2xl">
        {plan.intro}{" "}
        {!isMember && (
          <strong>Week one is here — start it today.</strong>
        )}
      </p>

      <div className="mt-6 space-y-4 max-w-2xl">
        <WeekOpen week={plan.weeks[0]} number={1} color={color} />
        {plan.weeks.slice(1).map((week, i) =>
          isMember ? (
            <WeekOpen key={week.title} week={week} number={i + 2} color={color} />
          ) : (
            <WeekLocked key={week.title} week={week} number={i + 2} />
          )
        )}
        <div
          className="bg-[#FBFAF6] border border-[#E6E3DB] border-l-4 p-5 rounded-sm"
          style={{ borderLeftColor: color }}
          data-testid="result-plan-days-29-60"
        >
          <p className="eyebrow" style={{ color }}>Days 29–60 · Run it on your Spending Plan</p>
          <p className="mt-2 text-sm text-[#2C302E] leading-relaxed">
            The habits above move onto your own numbers — bills, paydays, and savings on one
            calendar, with your Running Balance 30, 60, and 90 days ahead. Everything in this plan
            is available during your free 60 days.
          </p>
        </div>
      </div>

      {isMember ? (
        <p className="mt-5 text-sm text-[#787671] max-w-2xl">
          Work the plan from your{" "}
          <Link to="/app/habits" className="underline text-[#2C302E]">
            Habit Loop
          </Link>{" "}
          — your starter habit is already switched on.
        </p>
      ) : (
        <p className="mt-5 text-sm text-[#787671] max-w-2xl" data-testid="result-plan-unlock-note">
          Weeks 2–4 open the moment you create your free account — your 60 days begin then.
        </p>
      )}
    </section>
  );
};

const WeekOpen = ({ week, number, color }) => (
  <div
    className="bg-white border border-[#E6E3DB] p-5 rounded-sm"
    data-testid={`result-plan-week-${number}`}
  >
    <div className="flex items-baseline justify-between mb-2">
      <p className="eyebrow" style={{ color }}>Week {number} · {week.title}</p>
      {number === 1 && (
        <span className="text-[10px] uppercase tracking-wider text-[#3E6B37] font-semibold">
          Start today
        </span>
      )}
    </div>
    <ul className="space-y-2.5">
      {week.actions.map((a) => (
        <li key={a.slice(0, 32)} className="flex items-start gap-2.5">
          <Check className="h-4 w-4 mt-1 shrink-0" style={{ color }} />
          <span className="text-sm text-[#2C302E] leading-relaxed">{a}</span>
        </li>
      ))}
    </ul>
  </div>
);

const WeekLocked = ({ week, number }) => (
  <div
    className="relative bg-[#F2EFE7] border border-dashed border-[#C7C0B0] p-5 rounded-sm overflow-hidden"
    data-testid={`result-plan-week-${number}-locked`}
  >
    <div className="flex items-baseline justify-between mb-1">
      <p className="eyebrow text-[#9b9892]">Week {number} · {week.title}</p>
      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-[#A9712A] font-semibold">
        <Lock className="h-3 w-3" /> Locked
      </span>
    </div>
    <p className="text-sm text-[#4d4f4c] leading-relaxed">{week.teaser}</p>
    {/* Placeholder lines only — the real week actions never enter this DOM. */}
    <div aria-hidden className="mt-3 space-y-2.5 select-none pointer-events-none">
      {["Step one of this week's routine, tailored to your pattern",
        "Step two builds on it with one small daily action",
        "Step three locks the habit in place for good"].map((p) => (
        <div key={p} className="flex items-start gap-2.5">
          <Lock className="h-3.5 w-3.5 mt-0.5 shrink-0 text-[#C7C0B0]" />
          <span className="text-sm text-[#9b9892] leading-relaxed blur-[3px]">{p}</span>
        </div>
      ))}
    </div>
  </div>
);
